import { create } from 'zustand';

/**
 * Replaces `SectionService`.
 *
 * Which sub-section of an open activity is showing - "Subject Property",
 * "Comparables", "Argus", "ChangeHistory". The shell's sidebar and the activity
 * pages both read and write it, which is why it was a service rather than a
 * component field.
 *
 * The `localStorage` write is kept inside the setter deliberately:
 * the selection has to survive a full page reload, because MSAL's redirect
 * sign-in *is* a full page reload and users came back to the wrong tab without
 * it.
 */

const STORAGE_KEY = 'selectedSubsection';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Safari in private mode throws on any localStorage access.
    return null;
  }
}

export interface SectionState {
  selectedSubSection: string | null;
  setSelectedSubSection: (section: string) => void;
  clearSelectedSubSection: () => void;
}

export const useSectionStore = create<SectionState>((set) => ({
  selectedSubSection: readStored(),

  setSelectedSubSection: (section) => {
    set({ selectedSubSection: section });

    try {
      localStorage.setItem(STORAGE_KEY, section);
    } catch {
      /* storage unavailable - the in-memory value still stands for this session */
    }
  },

  clearSelectedSubSection: () => {
    set({ selectedSubSection: null });

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to clear */
    }
  }
}));

export const sectionStore = {
  getSelectedSection: () => useSectionStore.getState().selectedSubSection,
  setSelectedSubSection: (section: string) => useSectionStore.getState().setSelectedSubSection(section)
};
